import React, { useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { EffectComposer, DepthOfField } from "@react-three/postprocessing";
import * as THREE from "three";

function SpinningIcosahedron({ position, color = "#38bdf8", scale = 1, parallax = 0.25, opacity = 0.35 }) {
  const ref = useRef();
  const { mouse } = useThree();
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (ref.current) {
      ref.current.rotation.x = t * 0.2;
      ref.current.rotation.y = t * 0.3;
      ref.current.position.x = position[0] + mouse.x * parallax;
      ref.current.position.y = position[1] + mouse.y * (parallax * 0.6);
    }
  });
  return (
    <Float speed={1} rotationIntensity={0.4} floatIntensity={0.9}>
      <mesh ref={ref} position={position} scale={scale} castShadow receiveShadow>
        <icosahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color={color} transparent opacity={opacity} metalness={0.1} roughness={0.5} />
      </mesh>
    </Float>
  );
}

function CameraRig({ ease = 0.04, distance = 9 }) {
  const { camera, mouse } = useThree();
  const target = useRef(new THREE.Vector3());
  useFrame(() => {
    target.current.set(mouse.x * 1.2, mouse.y * 0.8, distance);
    camera.position.lerp(target.current, ease);
    camera.lookAt(0, 0, 0);
  });
  return null;
}

function Scene() {
  const groupRef = useRef();
  const qTarget = useRef(new THREE.Quaternion());
  useFrame(({ mouse }) => {
    if (!groupRef.current) return;
    const e = new THREE.Euler(mouse.y * -0.15, mouse.x * 0.2, 0);
    qTarget.current.setFromEuler(e);
    groupRef.current.quaternion.slerp(qTarget.current, 0.04);
  });

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[4, 6, 6]} intensity={0.5} />
      <group ref={groupRef}>
        {/* large, muted shapes spanning the viewport */}
        <SpinningIcosahedron position={[-4, 1.2, -1.8]} color="#38bdf8" scale={3.6} parallax={0.18} opacity={0.18} />
        <SpinningIcosahedron position={[4.2, -1.6, -1.2]} color="#22d3ee" scale={2.8} parallax={0.26} opacity={0.18} />
        <SpinningIcosahedron position={[0.6, 2.2, -0.6]} color="#67e8f9" scale={2.2} parallax={0.34} opacity={0.18} />
      </group>
      <EffectComposer>
        <DepthOfField focusDistance={0.02} focalLength={0.02} bokehScale={1.2} />
      </EffectComposer>
      <CameraRig />
    </>
  );
}

export default function ThreeHero() {
  return (
    <div className="bg-3d" aria-hidden>
      <Canvas camera={{ position: [0, 0, 9], fov: 55 }} dpr={[1, 2]}>
        <Scene />
      </Canvas>
    </div>
  );
}


